/**
 * Milestone Timeline Alpine.js Component
 *
 * Desktop: vertical drum/slot-machine year scroller with size + opacity transitions.
 * Mobile:  horizontal Swiper year picker (centeredSlides, 3 visible).
 *
 * Both views share the same `activeIndex` state, keeping images and
 * descriptions in sync across breakpoints.
 *
 * Usage (via x-data + x-init in Twig):
 *   x-data="milestoneTimeline(milestonesJsonArray)"
 *   x-init="init()"
 *
 * @param {Array} milestones - Array of { year, description, imageUrl, imageAlt }
 */
export function milestoneTimeline(milestones) {
  return {
    milestones,
    activeIndex: 0,
    _mobileSwiper: null,

    // ── Lifecycle ────────────────────────────────────────────────────
    init() {
      this.$nextTick(() => {
        const el = this.$refs.mobileYearSwiper;
        if (!el || typeof Swiper === "undefined") return;

        this._mobileSwiper = new Swiper(el, {
          slidesPerView: 3,
          centeredSlides: true,
          speed: 400,
          grabCursor: true,
          pagination: {
            el: this.$refs.mobileProgressBar,
            type: "progressbar",
          },
          on: {
            // Swipe navigation → update shared state
            slideChange: (swiper) => {
              this.activeIndex = swiper.activeIndex;
            },
          },
        });
      });
    },

    // ── Computed ─────────────────────────────────────────────────────
    get hasPrev() {
      return this.activeIndex > 0;
    },

    get hasNext() {
      return this.activeIndex < this.milestones.length - 1;
    },

    // ── Navigation ───────────────────────────────────────────────────
    prev() {
      if (this.hasPrev) {
        this.activeIndex--;
        this._mobileSwiper?.slidePrev();
      }
    },

    next() {
      if (this.hasNext) {
        this.activeIndex++;
        this._mobileSwiper?.slideNext();
      }
    },

    /** Navigate to a specific milestone (used by mobile slide click). */
    goTo(index) {
      this.activeIndex = index;
      this._mobileSwiper?.slideTo(index);
    },

    // ── Desktop Year Drum Scroller ───────────────────────────────────
    /**
     * Returns inline CSS for each year label based on its offset from the
     * active index — matching the Figma drum-roll layout:
     *
     *   offset  top     fontSize  opacity
     *   ──────  ──────  ────────  ───────
     *    -2+    hidden   28px      0
     *    -1       0px   58.76px   0.7
     *     0      64px   80px      1.0   ← active (tick marks sit at 94px)
     *    +1     144px   58.76px   0.7
     *    +2     208px   45.20px   0.3
     *    +3     262px   45.20px   0.3   (clipped by overflow:hidden)
     *    +4+    off      28px      0
     */
    getYearStyle(index) {
      const offset = index - this.activeIndex;

      // [topPx, fontSizePx, opacity]
      const map = {
        "-3": [-90, 28, 0],
        "-2": [-45, 33.56, 0],
        "-1": [0, 58.76, 0.7],
        0: [64, 80, 1],
        1: [144, 58.76, 0.7],
        2: [208, 45.2, 0.3],
        3: [262, 45.2, 0],
      };

      const clamped = Math.max(-3, Math.min(3, offset));
      const [top, fontSize, opacity] =
        map[clamped] ?? (offset < 0 ? [-90, 28, 0] : [310, 28, 0]);

      return [
        `top: ${top}px`,
        `font-size: ${fontSize}px`,
        `opacity: ${opacity}`,
        "transition: top 0.5s ease, font-size 0.5s ease, opacity 0.5s ease",
      ].join("; ");
    },
  };
}
